export const formatCurrency = (value, maximumFractionDigits = 0) =>
  new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits,
  }).format(Number(value || 0));

const normalizeLaunchDisplayPrice = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/(request|contact|call|ask)/i.test(text)) return "Price on request";
  if (/^from\s+/i.test(text)) return `From ${text.replace(/^from\s+/i, "")}`;
  return `From ${text}`;
};

export const formatNumber = (value) => new Intl.NumberFormat("en-SG").format(Number(value || 0));

export const formatListingPrice = (listing) => {
  if (listing?.category === "new_launch") {
    const displayPrice = normalizeLaunchDisplayPrice(listing?.display_price);
    if (displayPrice) return displayPrice;
    if (Number(listing?.price || 0) > 0) return `From ${formatCurrency(listing?.price)}`;
    return "Price on request";
  }
  return formatCurrency(listing?.price);
};

export const isListingPriceOnRequest = (listing) => formatListingPrice(listing) === "Price on request";

export const formatListingPsf = (listing) => {
  if (listing?.category === "new_launch" && Number(listing?.psf || 0) <= 0) return "Awaiting release";
  return formatCurrency(listing?.psf, 0);
};

export const formatListingMonthly = (listing) => {
  if (listing?.category === "new_launch" && Number(listing?.monthly_installment_estimate || 0) <= 0) return "Contact us";
  return formatCurrency(listing?.monthly_installment_estimate);
};

export const formatDate = (value) =>
  new Date(value).toLocaleDateString("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export const formatCategoryLabel = (value) =>
  ({ buy: "Buy", rent: "Rent", new_launch: "New Launch" }[value] || value);

export const calculateBSD = (amount) => {
  let remaining = Number(amount || 0);
  const tiers = [
    [180000, 0.01],
    [180000, 0.02],
    [640000, 0.03],
    [500000, 0.04],
    [1500000, 0.05],
    [Infinity, 0.06],
  ];

  return tiers.reduce((total, [limit, rate]) => {
    if (remaining <= 0) return total;
    const taxable = Math.min(remaining, limit);
    remaining -= taxable;
    return total + taxable * rate;
  }, 0);
};

export const calculateABSD = ({ amount, buyerType, propertyCount }) => {
  const price = Number(amount || 0);
  const normalizedCount = Number(propertyCount || 1);
  let rate = 0;

  if (buyerType === "SC") rate = normalizedCount === 1 ? 0 : normalizedCount === 2 ? 0.2 : 0.3;
  if (buyerType === "SPR") rate = normalizedCount === 1 ? 0.05 : normalizedCount === 2 ? 0.3 : 0.35;
  if (buyerType === "Foreigner") rate = 0.6;

  return { rate, duty: price * rate };
};

export const calculatePropertyTaxRebate = ({ annualTax, propertyType, isOwnerOccupier }) => {
  const tax = Number(annualTax || 0);
  if (!isOwnerOccupier || tax <= 0) {
    return { rate: 0, rebate: 0, capped: false };
  }

  if (propertyType === "HDB") {
    return { rate: 0.15, rebate: tax * 0.15, capped: false };
  }

  const rawRebate = tax * 0.1;
  return { rate: 0.1, rebate: Math.min(rawRebate, 500), capped: rawRebate > 500 };
};

export const calculateMonthlyMortgage = ({ principal, annualRate, tenureYears }) => {
  const loan = Number(principal || 0);
  const monthlyRate = Number(annualRate || 0) / 100 / 12;
  const periods = Number(tenureYears || 0) * 12;

  if (!loan || !periods) return 0;
  if (monthlyRate === 0) return loan / periods;

  const growth = (1 + monthlyRate) ** periods;
  return loan * ((monthlyRate * growth) / (growth - 1));
};

export const calculateMortgageSnapshot = ({ price, income, debts, annualRate, tenureYears, isHdb }) => {
  const propertyPrice = Number(price || 0);
  const monthlyIncome = Number(income || 0);
  const monthlyDebts = Number(debts || 0);
  const ltvMax = propertyPrice * 0.75;
  const maxDebtServicing = Math.max(monthlyIncome * 0.55 - monthlyDebts, 0);
  const maxMortgageByMsr = isHdb ? Math.max(monthlyIncome * 0.3 - monthlyDebts, 0) : Infinity;
  const permittedMonthly = Math.min(maxDebtServicing, maxMortgageByMsr);
  const rate = Number(annualRate || 0) / 100 / 12;
  const periods = Number(tenureYears || 0) * 12;
  let maxLoanByIncome = 0;

  if (permittedMonthly > 0 && periods > 0) {
    if (rate === 0) maxLoanByIncome = permittedMonthly * periods;
    else {
      const growth = (1 + rate) ** periods;
      maxLoanByIncome = (permittedMonthly * (growth - 1)) / (rate * growth);
    }
  }

  const approvedLoan = Math.min(ltvMax, maxLoanByIncome || 0);
  const monthlyInstallment = calculateMonthlyMortgage({ principal: approvedLoan, annualRate, tenureYears });

  return {
    ltvMax,
    approvedLoan,
    monthlyInstallment,
    cashDownpayment: Math.max(propertyPrice - approvedLoan, 0),
    permittedMonthly,
  };
};

export const buildDistrictSeoCopy = (district, propertyType) => ({
  title: `${propertyType ? `${propertyType}s` : "Homes"} for sale in ${district} Singapore | SG Prop Portal`,
  description: `Browse curated ${propertyType ? propertyType.toLowerCase() : "residential"} listings in ${district}, Singapore with live district filters, PSF insights, and direct owner-agent contact.`,
});